import { TextLink, theme } from '@ovotech/element-v5';
import { Card } from '@ovotech/nebula';
import React from 'react';
import styled from 'styled-components';
import { breakpoints, breakpointVals } from '@/src/utils/mediaQuery';
import { ReactComponent as AppleStoreBadge } from '@/src/resources/apple-store-badge.svg';
import { ReactComponent as GooglePlayBadge } from '@/src/resources/google-play-badge-2026.svg';
import QRCode from '@/src/resources/get-app-qr.png';
import {
  APPLE_APP_STORE_URL,
  GOOGLE_PLAY_STORE_URL,
} from '@/src/components/Banners/GetBeyondBanner/constants';

type StyledCardProps = {
  variant: 'green' | 'white';
};

export const StyledCard = styled(Card)<StyledCardProps>`
  background-color: ${({ variant }) =>
    variant === 'green' ? theme.color.brand.dim : theme.color.neutral.white};
  color: ${({ variant }) =>
    variant === 'green' ? theme.color.neutral.white : 'inherit'};
  display: flex;
  flex-direction: column;
  position: relative;
  padding: ${theme.space['800']};
  container-type: inline-size;

  @media ${breakpoints.mediumDown} {
    overflow: hidden;
  }
`;

export const BeyondText = styled.p`
  color: ${theme.color.brand.bold};
  font-size: ${theme.font.size.lg.mediaQuery.lg};
  font-weight: ${theme.font.weight.bold};
  line-height: ${theme.font.lineHeight.lg.mediaQuery};
  letter-spacing: ${theme.font.letterSpacing.lg.mediaQuery};

  @media ${breakpoints.mediumDown} {
    font-size: ${theme.font.size.sm.mediaQuery.lg};
    line-height: ${theme.font.lineHeight.sm.mediaQuery.lg};
    letter-spacing: ${theme.font.letterSpacing.sm.mediaQuery.lg};
  }

  @media ${breakpoints.mediumUp} {
    margin-bottom: 8px;
  }
`;

export const RewardsText = styled.h2`
  font-size: ${theme.font.size['4xl'].mediaQuery.lg};
  font-weight: ${theme.font.weight.bold};
  line-height: ${theme.font.lineHeight['4xl'].mediaQuery.lg};
  letter-spacing: ${theme.font.letterSpacing['4xl'].mediaQuery.lg};

  @media ${breakpoints.mediumDown} {
    font-size: ${theme.font.size['3xl'].mediaQuery.lg};
    line-height: ${theme.font.lineHeight['3xl'].mediaQuery.lg};
    letter-spacing: ${theme.font.letterSpacing['3xl'].mediaQuery.lg};
  }
`;

export const RewardsSubText = styled.span`
  display: block;
  font-size: ${theme.font.size['2xl'].mediaQuery.lg};
  font-weight: ${theme.font.weight.bold};
  line-height: ${theme.font.lineHeight['2xl'].mediaQuery.lg};
  letter-spacing: ${theme.font.letterSpacing['2xl'].mediaQuery.lg};

  @media ${breakpoints.mediumDown} {
    font-size: ${theme.font.size.lg.mediaQuery.lg};
    line-height: ${theme.font.lineHeight.lg.mediaQuery.lg};
    letter-spacing: ${theme.font.letterSpacing.lg.mediaQuery.lg};
  }
`;

type StyledPhoneImageProps = {
  hideOnMobile?: boolean;
};

export const StyledPhoneImage = styled.img<StyledPhoneImageProps>`
  position: absolute;
  bottom: 0;
  right: 13px;
  height: 104%;

  @media ${breakpoints.mediumDown} {
    display: ${({ hideOnMobile }) => (hideOnMobile ? 'none' : 'block')};
    right: 0;
    max-height: 62%;
    height: auto;
    max-width: 55%;
  }

  @media (min-width: ${breakpointVals.medium + 1}px) and (max-width: 800px) {
    right: 0;
    height: auto;
    max-width: 40%;
  }

  @media (max-width: 350px) {
    display: none;
  }
`;

export const StyledTextLink = styled(TextLink)`
  font-weight: ${theme.font.weight.book};
`;

export const AppBadgesContainer = styled.div`
  display: flex;
  position: relative;
  gap: ${theme.space['300']};
  align-items: flex-end;

  @media ${breakpoints.mediumDown} {
    flex-direction: column;
  }
`;

export const AppBadge = styled.a`
  display: flex;
  height: 44px;

  z-index: 1;

  svg {
    height: 100%;
    width: auto;
  }

  @media ${breakpoints.mediumDown} {
    height: auto;
    width: 150px;

    svg {
      width: 100%;
      height: auto;
    }
  }
`;

export const QRCodeWrapper = styled.div`
  align-self: flex-start;
  margin-left: 33px;

  @media (min-width: ${breakpointVals.navigation}px) and (max-width: 1075px) {
    margin-left: 0;
  }

  @media (min-width: ${breakpointVals.medium}px) and (max-width: 820px) {
    margin-left: 0;
  }

  @media ${breakpoints.mediumDown} {
    display: none;
  }
`;

export const BeyondRewardsHeading = () => (
  <div>
    <BeyondText>Beyond</BeyondText>
    <RewardsText>
      Rewards
      <RewardsSubText>just for being with us</RewardsSubText>
    </RewardsText>
  </div>
);

export type AppStoreBadgeProps = {
  onAppStoreClick?: () => void;
  onGooglePlayClick?: () => void;
};

export const AppStoreBadges = ({
  onAppStoreClick,
  onGooglePlayClick,
}: AppStoreBadgeProps) => (
  <AppBadgesContainer>
    <AppBadge
      href={APPLE_APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Apple App Store"
      onClick={onAppStoreClick}
    >
      <AppleStoreBadge />
    </AppBadge>
    <AppBadge
      href={GOOGLE_PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Google Play Store"
      onClick={onGooglePlayClick}
    >
      <GooglePlayBadge />
    </AppBadge>
    <QRCodeWrapper>
      <img src={QRCode} alt="" width="88" height="88" />
    </QRCodeWrapper>
  </AppBadgesContainer>
);
